import { EmailReply } from '@/hooks/useEmailReplies';

interface RepliesCache {
  replies: EmailReply[];
  timestamp: number;
  pagination: {
    page: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
    total: number;
  };
}

const CACHE_EXPIRY = 5 * 60 * 1000; // 5 minutes

export const getRepliesFromCache = (emailId: string): RepliesCache | null => {
  try {
    const cached = localStorage.getItem(`email_replies_${emailId}`);
    if (!cached) return null;

    const data = JSON.parse(cached) as RepliesCache;
    
    // Check if cache is expired
    if (Date.now() - data.timestamp > CACHE_EXPIRY) {
      localStorage.removeItem(`email_replies_${emailId}`);
      return null;
    }

    return data;
  } catch (error) {
    console.error('Error reading replies from cache:', error);
    return null;
  }
};

export const saveRepliesToCache = (
  emailId: string, 
  replies: EmailReply[], 
  pagination: {
    page: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
    total: number;
  }
) => {
  try {
    const data: RepliesCache = {
      replies,
      timestamp: Date.now(),
      pagination
    };
    localStorage.setItem(`email_replies_${emailId}`, JSON.stringify(data));
  } catch (error) {
    console.error('Error saving replies to cache:', error);
  }
};

export const updateReplyInCache = (emailId: string, replyId: string, flags: string[]) => {
  try {
    const cached = getRepliesFromCache(emailId);
    if (!cached) return;

    const updatedReplies = cached.replies.map(reply => {
      if (reply.id === replyId) {
        return { ...reply, flags };
      }
      return reply;
    });

    saveRepliesToCache(emailId, updatedReplies, cached.pagination);
  } catch (error) {
    console.error('Error updating reply in cache:', error);
  }
};

export const addReplyToCache = (emailId: string, newReply: EmailReply) => {
  try {
    const cached = getRepliesFromCache(emailId);
    if (!cached) {
      // If no cache exists, create new cache with just this reply
      saveRepliesToCache(emailId, [newReply], {
        page: 1,
        totalPages: 1,
        hasNext: false,
        hasPrev: false,
        total: 1
      });
      return;
    }

    // Add new reply to the beginning of the list
    const updatedReplies = [newReply, ...cached.replies];
    
    // Update pagination
    const updatedPagination = {
      ...cached.pagination,
      total: cached.pagination.total + 1
    };

    saveRepliesToCache(emailId, updatedReplies, updatedPagination);
  } catch (error) {
    console.error('Error adding reply to cache:', error);
  }
};

export const clearRepliesCache = (emailId: string) => {
  try {
    localStorage.removeItem(`email_replies_${emailId}`);
  } catch (error) {
    console.error('Error clearing replies cache:', error);
  }
};
